import type { ReportKey, ReportRangePreset, SavedFilter } from "@station/contracts";
import { type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateRangePicker,
  Dialog,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Label,
  Select,
  useToast,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bookmark, ChevronDown, FileSpreadsheet, FileText, Star, Trash2 } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { queryKeys } from "../../lib/query.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { downloadBlob } from "../accounting/shared.js";
import { fileNameFromContentDisposition } from "../ev/ev-values.js";
import { compactSearch } from "../shifts/shift-values.js";
import { useReportPermissions, useSavedFilterMutations, useSavedFilters } from "./queries.js";
import {
  exportQuery,
  filterParams,
  presetOf,
  rangeError,
  type ReportFilterState,
  resolveRangePreset,
  stateFromParams,
} from "./report-values.js";

// ── URL arama durumu ──────────────────────────────────────────────────────────
const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const uuidText = z.string().uuid().optional().catch(undefined);

/** Rapor ekranlarının ortak URL arama alanları (süzgeç paylaşılabilir / yer imine eklenebilir). */
export const reportSearchShape = {
  from: localDateText,
  to: localDateText,
  preset: z
    .enum(["TODAY", "YESTERDAY", "LAST_7_DAYS", "LAST_30_DAYS", "THIS_MONTH", "LAST_MONTH"])
    .optional()
    .catch(undefined),
  companyId: uuidText,
  stations: z.string().optional().catch(undefined),
  stationId: uuidText,
  windowDays: z.coerce.number().int().min(1).max(14).optional().catch(undefined),
};
export type ReportSearch = {
  from?: string | undefined;
  to?: string | undefined;
  preset?: ReportRangePreset | undefined;
  companyId?: string | undefined;
  stations?: string | undefined;
  stationId?: string | undefined;
  windowDays?: number | undefined;
};

/** Arama → süzgeç durumu (boşsa varsayılan hazır ayar). */
export function filterStateFromSearch(
  search: ReportSearch,
  defaultPreset: ReportRangePreset,
  today: LocalDate = toIstanbulDate(new Date()),
): ReportFilterState {
  const preset = search.preset ?? (search.from && search.to ? null : defaultPreset);
  const range =
    search.from && search.to
      ? { from: search.from as LocalDate, to: search.to as LocalDate }
      : resolveRangePreset(preset ?? defaultPreset, today);
  return {
    range,
    preset: preset ?? presetOf(range, today),
    companyId: search.companyId ?? null,
    stationIds: search.stations ? search.stations.split(",").filter(Boolean) : [],
    stationId: search.stationId ?? null,
    windowDays: search.windowDays ?? 3,
  };
}

/** Süzgeç durumu → arama (hazır ayar varsa tarih YAZILMAZ; bağlantı yarın açılınca da "son 30 gün" olur). */
export function searchFromFilterState(state: ReportFilterState): ReportSearch {
  return compactSearch({
    ...(state.preset ? { preset: state.preset } : { from: state.range.from, to: state.range.to }),
    companyId: state.companyId ?? undefined,
    stations: state.stationIds.length > 0 ? state.stationIds.join(",") : undefined,
    stationId: state.stationId ?? undefined,
    windowDays: state.windowDays && state.windowDays !== 3 ? state.windowDays : undefined,
  }) as ReportSearch;
}

/** Sayfanın süzgeç durumu + güncelleyici (URL'ye yazar). */
export function useReportFilter(
  search: ReportSearch,
  defaultPreset: ReportRangePreset,
): [ReportFilterState, (next: ReportFilterState) => void, boolean] {
  const navigate = useNavigate();
  const state = filterStateFromSearch(search, defaultPreset);
  const explicit = Object.values(search).some((value) => value !== undefined);
  const set = (next: ReportFilterState) =>
    void navigate({ to: ".", search: searchFromFilterState(next) as never, replace: true });
  return [state, set, explicit];
}

// ── Erişim kapısı ─────────────────────────────────────────────────────────────
export function ReportsGate({
  need = "read",
  children,
}: {
  need?: "read" | "schedule" | "settings";
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useReportPermissions();
  if (!permissions.enabled)
    return (
      <EmptyState
        title={t("reports.gate.disabledTitle")}
        description={t("reports.gate.disabledDescription")}
      />
    );
  const allowed =
    need === "schedule"
      ? permissions.canSchedule
      : need === "settings"
        ? permissions.canManageSettings
        : permissions.canRead;
  if (!allowed)
    return (
      <EmptyState
        title={t("reports.gate.forbiddenTitle")}
        description={t("reports.gate.forbiddenDescription")}
      />
    );
  return <>{children}</>;
}

// ── Şirket / istasyon seçenekleri ─────────────────────────────────────────────
export function useScopeOptions() {
  const companies = useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => (await api.GET("/companies")).data!.items,
  });
  const stations = useQuery({
    queryKey: queryKeys.stations({ reports: true }),
    queryFn: async () => (await api.GET("/stations", { params: { query: { limit: 200 } } })).data!.items,
  });
  return {
    companies: companies.data ?? [],
    stations: (stations.data ?? []).filter((station) => station.status === "ACTIVE"),
    loading: companies.isPending || stations.isPending,
  };
}

// ── Süzgeç çubuğu ─────────────────────────────────────────────────────────────
export interface ReportFilterBarProps {
  reportKey: ReportKey;
  state: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
  /** İstasyon seçimi: çoklu (karşılaştırma), tekli (akaryakıt analitiği) ya da yok. */
  stations?: "multi" | "single" | "none";
  /** Dönem yok (finans özeti anlık durumdur). */
  range?: boolean;
  /** Şirket seçimi zorunlu (kârlılık, tutarlılık). */
  companyRequired?: boolean;
  maxDays: number;
  /** Rapora özgü ek denetimler (ör. fiyat değişimi penceresi). */
  extra?: ReactNode;
  /** Dışa aktarım ek parametreleri (kârlılık). */
  exportExtra?: { groupBy?: string; includeDrafts?: boolean };
  /** URL'de açık süzgeç var mı (varsa varsayılan kaydedilmiş filtre uygulanmaz). */
  explicit?: boolean;
}

export function ReportFilterBar({
  reportKey,
  state,
  onChange,
  stations = "multi",
  range = true,
  companyRequired = false,
  maxDays,
  extra,
  exportExtra,
  explicit = false,
}: ReportFilterBarProps): ReactElement {
  const { t } = useTranslation();
  const shape = useTenantShape();
  const options = useScopeOptions();
  const companyId = useId();
  const stationSelectId = useId();
  const stationsLabelId = useId();
  const stationsValueId = useId();
  const today = toIstanbulDate(new Date());
  const error = range ? rangeError(state.range, maxDays) : null;
  const scopedStations = options.stations.filter(
    (station) => !state.companyId || station.companyId === state.companyId,
  );
  const showCompany = options.companies.length > 1 || companyRequired;
  const showStations = stations !== "none" && !shape.isSingleStation && scopedStations.length > 1;

  return (
    <Card className="grid gap-4 p-4" role="search" aria-label={t("reports.filter.label")}>
      <div className="flex flex-wrap items-start gap-4">
        {range ? (
          <div className="grid gap-1.5">
            <span className="text-sm font-medium" aria-hidden="true">
              {t("reports.filter.range")}
            </span>
            <DateRangePicker
              label={t("reports.filter.range")}
              value={{ from: state.range.from, to: state.range.to }}
              onValueChange={(next) => {
                if (!next.from || !next.to) return;
                const nextRange = { from: next.from, to: next.to };
                onChange({ ...state, range: nextRange, preset: presetOf(nextRange, today) });
              }}
            />
          </div>
        ) : null}
        {showCompany ? (
          <div className="grid gap-1.5">
            <Label htmlFor={companyId}>{t("reports.filter.company")}</Label>
            <Select
              id={companyId}
              className="w-56"
              value={state.companyId ?? ""}
              onChange={(event) =>
                onChange({ ...state, companyId: event.target.value || null, stationIds: [], stationId: null })
              }
            >
              {companyRequired ? null : <option value="">{t("reports.filter.allCompanies")}</option>}
              {options.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.shortName ?? company.legalName}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {showStations && stations === "single" ? (
          <div className="grid gap-1.5">
            <Label htmlFor={stationSelectId}>{t("reports.filter.station")}</Label>
            <Select
              id={stationSelectId}
              className="w-56"
              value={state.stationId ?? ""}
              onChange={(event) => onChange({ ...state, stationId: event.target.value || null })}
            >
              <option value="">{t("reports.filter.allStations")}</option>
              {scopedStations.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} · {station.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {showStations && stations === "multi" ? (
          <div className="grid gap-1.5">
            <span id={stationsLabelId} className="text-sm font-medium">
              {t("reports.filter.stations")}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-56 justify-between"
                  aria-labelledby={`${stationsLabelId} ${stationsValueId}`}
                >
                  <span id={stationsValueId} className="truncate">
                    {state.stationIds.length === 0
                      ? t("reports.filter.allStations")
                      : t("reports.filter.stationsSelected", { count: state.stationIds.length })}
                  </span>
                  <ChevronDown aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                <DropdownMenuItem onSelect={() => onChange({ ...state, stationIds: [] })}>
                  {t("reports.filter.allStations")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {scopedStations.map((station) => (
                  <DropdownMenuCheckboxItem
                    key={station.id}
                    checked={state.stationIds.includes(station.id)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) =>
                      onChange({
                        ...state,
                        stationIds: checked
                          ? [...state.stationIds, station.id]
                          : state.stationIds.filter((id) => id !== station.id),
                      })
                    }
                  >
                    {station.code} · {station.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
        {extra}
        <div className="ml-auto flex flex-wrap items-end gap-2 self-end">
          <SavedFiltersMenu reportKey={reportKey} state={state} onApply={onChange} explicit={explicit} />
          <ExportButtons reportKey={reportKey} state={state} disabled={Boolean(error)} extra={exportExtra} />
        </div>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
    </Card>
  );
}

// ── Dışa aktarım ──────────────────────────────────────────────────────────────
export function ExportButtons({
  reportKey,
  state,
  disabled = false,
  extra,
}: {
  reportKey: ReportKey;
  state: ReportFilterState;
  disabled?: boolean;
  extra?: { groupBy?: string; includeDrafts?: boolean } | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState<"XLSX" | "PDF" | null>(null);

  async function download(format: "XLSX" | "PDF"): Promise<void> {
    setBusy(format);
    try {
      const { data, response } = await api.GET("/reports/{reportKey}/export", {
        params: { path: { reportKey }, query: exportQuery(reportKey, format, state, extra) as never },
        parseAs: "blob",
      });
      downloadBlob(
        data as Blob,
        fileNameFromContentDisposition(response.headers.get("content-disposition")) ??
          `rapor.${format === "XLSX" ? "xlsx" : "pdf"}`,
      );
      toast({ tone: "success", title: t("reports.export.downloaded") });
    } catch (caught) {
      toast({ tone: "danger", title: t("reports.export.failed"), description: errorMessage(caught) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex gap-2" role="group" aria-label={t("reports.export.label")}>
      <Button
        variant="outline"
        onClick={() => void download("XLSX")}
        loading={busy === "XLSX"}
        disabled={disabled}
      >
        <FileSpreadsheet aria-hidden="true" />
        {t("reports.export.excel")}
      </Button>
      <Button
        variant="outline"
        onClick={() => void download("PDF")}
        loading={busy === "PDF"}
        disabled={disabled}
      >
        <FileText aria-hidden="true" />
        {t("reports.export.pdf")}
      </Button>
    </div>
  );
}

// ── Kaydedilmiş filtreler ─────────────────────────────────────────────────────
export function SavedFiltersMenu({
  reportKey,
  state,
  onApply,
  applyDefault = true,
  explicit = false,
}: {
  reportKey: ReportKey;
  state: ReportFilterState;
  onApply: (next: ReportFilterState) => void;
  /** Sayfa ilk açıldığında (URL'de süzgeç yoksa) varsayılan filtre uygulanır. */
  applyDefault?: boolean;
  explicit?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const filters = useSavedFilters(reportKey);
  const mutations = useSavedFilterMutations(reportKey);
  const [saving, setSaving] = useState(false);
  const applied = useRef(false);
  const today = toIstanbulDate(new Date());

  useEffect(() => {
    if (applied.current || !applyDefault || explicit || !filters.data) return;
    applied.current = true;
    const preferred = filters.data.find((filter) => filter.isDefault);
    if (preferred) onApply(stateFromParams(preferred.params, state, today));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.data]);

  const apply = (filter: SavedFilter) => {
    onApply(stateFromParams(filter.params, state, today));
    toast({ tone: "info", title: t("reports.filters.applied", { name: filter.name }) });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Bookmark aria-hidden="true" />
            {t("reports.filters.menu")}
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t("reports.filters.saved")}</DropdownMenuLabel>
          {(filters.data ?? []).length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">{t("reports.filters.none")}</p>
          ) : (
            (filters.data ?? []).map((filter) => (
              <DropdownMenuItem
                key={filter.id}
                onSelect={() => apply(filter)}
                className="justify-between gap-2"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {filter.isDefault ? (
                    <Star
                      className="size-3.5 fill-current text-amber-600"
                      aria-label={t("reports.filters.default")}
                    />
                  ) : null}
                  <span className="truncate">{filter.name}</span>
                </span>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSaving(true)}>
            {t("reports.filters.saveCurrent")}
          </DropdownMenuItem>
          {(filters.data ?? []).length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("reports.filters.manage")}</DropdownMenuLabel>
              {(filters.data ?? []).map((filter) => (
                <DropdownMenuItem
                  key={`delete-${filter.id}`}
                  onSelect={() =>
                    mutations.remove.mutate(filter, {
                      onSuccess: () => toast({ tone: "success", title: t("reports.filters.deleted") }),
                      onError: (caught) =>
                        toast({
                          tone: "danger",
                          title: t("reports.filters.failed"),
                          description: errorMessage(caught),
                        }),
                    })
                  }
                >
                  <Trash2 aria-hidden="true" className="text-destructive" />
                  {t("reports.filters.deleteNamed", { name: filter.name })}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <SaveFilterDialog
        open={saving}
        onOpenChange={setSaving}
        onSave={async (name, isDefault) => {
          await mutations.create.mutateAsync({ name, isDefault, params: filterParams(reportKey, state) });
          toast({ tone: "success", title: t("reports.filters.savedToast") });
        }}
        hasPreset={state.preset !== null}
      />
    </>
  );
}

function SaveFilterDialog({
  open,
  onOpenChange,
  onSave,
  hasPreset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, isDefault: boolean) => Promise<void>;
  hasPreset: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const nameId = useId();
  const defaultId = useId();
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setIsDefault(false);
    setError(null);
  }, [open]);

  async function submit(): Promise<void> {
    if (!name.trim()) {
      setError(t("reports.filters.nameRequired"));
      return;
    }
    setBusy(true);
    try {
      await onSave(name.trim(), isDefault);
      onOpenChange(false);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("reports.filters.saveTitle")}
      description={hasPreset ? t("reports.filters.saveHintPreset") : t("reports.filters.saveHintFixed")}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={busy}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor={nameId}>{t("reports.filters.name")}</Label>
          <Input
            id={nameId}
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <label htmlFor={defaultId} className="flex items-center gap-2 text-sm">
          <Checkbox
            id={defaultId}
            checked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          />
          {t("reports.filters.makeDefault")}
        </label>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </form>
    </Dialog>
  );
}

// ── Küçük yapı taşları ────────────────────────────────────────────────────────
export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}): ReactElement {
  return (
    <div className="grid gap-1 rounded-md border p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="tabular text-xl font-semibold">{value}</span>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

export function SectionCard({
  title,
  description,
  actions,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
}): ReactElement {
  const generated = useId();
  const headingId = id ?? generated;
  return (
    <Card className="grid min-w-0 gap-4 p-5 [&>*]:min-w-0" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <h2 id={headingId} className="text-base font-semibold">
            {title}
          </h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </Card>
  );
}

/** Tablo sarmalayıcı: dar ekranda yatay kaydırılır (klavyeyle de — axe `scrollable-region-focusable`). */
export function TableScroll({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="overflow-x-auto rounded-md border" tabIndex={0} role="group" aria-label={label}>
      {children}
    </div>
  );
}
